import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import { getStoredRefreshToken, useAuthStore } from "@/lib/auth/authStore";
import { queryClient } from "@/lib/query/queryClient";
import type { ApiErrorBody, NormalizedApiError } from "@/types/api";
import { API_BASE_URL } from "@/lib/api/config";
import { extractTokenPair } from "@/lib/api/response";
import { i18n } from "@/i18n";

interface TokenPairResponse {
  access_token: string;
  refresh_token: string;
}

interface RefreshResponse {
  ok: boolean;
  tokens: TokenPairResponse;
}

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

type RefreshSubscriber = (token: string | null) => void;

let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

const notifyRefreshSubscribers = (token: string | null): void => {
  refreshSubscribers.forEach((subscriber) => subscriber(token));
  refreshSubscribers = [];
};

const waitForRefresh = (): Promise<string | null> =>
  new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });

/**
 * Krijon klientin Axios me base URL nga VITE_API_URL (ose fallback /api/v1).
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // Serializo array-t pa brackets: category=X&category=Y (jo category[]=X)
  // FastAPI Query(list[str]) kërkon parametra te perserituar pa brackets.
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) => {
      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) {
          v.forEach((item) => sp.append(k, String(item)));
        } else {
          sp.set(k, String(v));
        }
      });
      return sp.toString();
    },
  },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;

  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    const isRefreshRequest = typeof originalRequest?.url === "string" && originalRequest.url.includes("/auth/refresh");

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry || isRefreshRequest) {
      return Promise.reject(normalizeApiError(error));
    }

    const refreshToken = getStoredRefreshToken();

    if (!refreshToken) {
      clearSensitiveSession();
      redirectToLogin();
      return Promise.reject(normalizeApiError(error));
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      const queuedToken = await waitForRefresh();

      if (!queuedToken) {
        return Promise.reject(normalizeApiError(error));
      }

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${queuedToken}`,
      };

      return apiClient(originalRequest);
    }

    isRefreshing = true;

    try {
      const refreshResponse = await axios.post<RefreshResponse>(`${API_BASE_URL}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const nextTokens = extractTokenPair(refreshResponse.data);

      if (!nextTokens) {
        throw new Error("Refresh response nuk përmban token valid.");
      }

      useAuthStore.getState().setTokens({
        accessToken: nextTokens.access_token,
        refreshToken: nextTokens.refresh_token,
      });
      notifyRefreshSubscribers(nextTokens.access_token);

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${nextTokens.access_token}`,
      };

      return apiClient(originalRequest);
    } catch (refreshError) {
      notifyRefreshSubscribers(null);
      clearSensitiveSession();
      redirectToLogin();
      return Promise.reject(normalizeApiError(refreshError));
    } finally {
      isRefreshing = false;
    }
  },
);

/**
 * Nxjerr mesazh te qarte gabimi nga pergjigjet e API-se.
 */
export const getApiErrorMessage = (error: unknown): string => {
  if (isNormalizedApiError(error)) {
    return error.message;
  }

  return normalizeApiError(error).message;
};

/**
 * Normalizon cdo gabim API ne nje forme te vetme qe komponentet te mos lexojne Axios direkt.
 */
export const normalizeApiError = (error: unknown): NormalizedApiError => {
  if (isNormalizedApiError(error)) {
    return error;
  }

  if (axios.isAxiosError<ApiErrorBody>(error)) {
    const status = error.response?.status ?? null;
    const body = error.response?.data;
    const apiMessage = extractApiMessage(body, status);
    const message = translateApiMessage(apiMessage);

    return {
      status,
      code: body?.code ?? extractApiCode(body) ?? codeForStatus(status),
      message,
      kind: kindForStatus(status),
      fieldErrors: body?.fieldErrors,
      missingFields: extractMissingFields(body),
    };
  }

  return {
    status: null,
    code: "unknown",
    message: error instanceof Error ? error.message : "Ndodhi nje gabim i papritur.",
    kind: "unknown",
  };
};

export const isNormalizedApiError = (error: unknown): error is NormalizedApiError => {
  return typeof error === "object" && error !== null && "kind" in error && "message" in error && "code" in error;
};

const kindForStatus = (status: number | null): NormalizedApiError["kind"] => {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 400 || status === 422) return "validation";
  if (status === 501) return "module_inactive";
  return "unknown";
};

const codeForStatus = (status: number | null): string => {
  if (status === null) return "network_or_unknown";
  return `http_${status}`;
};

const messageForStatus = (status: number | null): string => {
  if (status === 403) return "errors.ACCESS_DENIED";
  if (status === 404) return "errors.NOT_FOUND";
  if (status === 501) return "Moduli nuk eshte aktivizuar ende.";
  if (status === 409) return "Kerkesa eshte ne konflikt me gjendjen aktuale.";
  if (status === 400 || status === 422) return "Kontrollo fushat dhe provo perseri.";
  if (status === 401) return "Sesioni ka skaduar.";
  return "Kerkesa nuk u krye me sukses.";
};

const extractApiMessage = (body: ApiErrorBody | undefined, status: number | null): string => {
  const detail = body?.detail;
  if (detail && typeof detail === "object") {
    if (detail.message_key) return `errors.${detail.message_key}`;
    if (detail.message) return detail.message;
  }
  if (typeof detail === "string") return detail;
  return body?.message ?? messageForStatus(status);
};

const extractApiCode = (body: ApiErrorBody | undefined): string | null => {
  const detail = body?.detail;
  if (detail && typeof detail === "object" && detail.message_key) {
    return detail.message_key;
  }
  return null;
};

const extractMissingFields = (body: ApiErrorBody | undefined): NormalizedApiError["missingFields"] => {
  const detail = body?.detail;
  if (!detail || typeof detail !== "object" || !Array.isArray(detail.missing_fields)) {
    return undefined;
  }
  return detail.missing_fields
    .filter((item) => item && typeof item.field_key === "string")
    .map((item) => ({ field_key: item.field_key, label: item.label }));
};

const translateApiMessage = (message: string): string => (
  message.startsWith("errors.") || message.startsWith("logs.") ? i18n.t(message) : message
);

const redirectToLogin = (): void => {
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`);
  }
};

const clearSensitiveSession = (): void => {
  useAuthStore.getState().logoutLocal();
  queryClient.clear();
};
